import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TokenInterceptorService } from './services/token-interceptor-service/token-interceptor.service';
import { PersonaService } from './services/persona-service/persona.service';
import { CountryService } from './services/country-service/country.service';
import { PhoneCodeService } from './services/phone-code-service/phone-code.service';
import { AddressService } from './services/address-service/address.service';
import { ProfileApiService } from './services/profile-api-service/profile-api.service';
import { ProfileService } from './services/profile-service/profile.service';
import { OnboardAuthService } from './services/onboard-auth-service/onboard-auth.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { CopyrightComponent } from './components/copyright/copyright.component';
import { CapfirstPipe } from './pipes/capfirst-pipe/capfirst.pipe';
import { InputMaskDirective } from './directives/input-mask/input-mask.directive';
import { TosCardsComponent } from './components/tos-cards/tos-cards.component';
import { TosDialogComponent } from './components/tos-dialog/tos-dialog.component';
import { FeatureFlagDirective } from './directives/feature-flag/feature-flag.directive';
import { FeatureFlagService } from './services/feature-flag/feature-flag.service';
import { CallbackComponent } from './components/callback/callback.component';
import { LoginComponent } from './components/login/login.component';
import { AuthService } from './services/auth.service';
import { Auth0Service } from './services/auth0.service';
import { ApplicationInsightsErrorHandler } from './services/error-handler-service/error-handler.service';
import { OnetrustPrivacyPolicyComponent } from './components/onetrust-privacy-policy/onetrust-privacy-policy.component';
import { OnetrustTosComponent } from './components/onetrust-tos/onetrust-tos.component';
import { TopnavOnetrustComponent } from './components/topnavs/topnav-onetrust/topnav-onetrust.component';
import { TopnavProgressiveProfileComponent } from './components/topnavs/topnav-progressive-profile/topnav-progressive-profile.component';
import { ProgressiveProfilePageComponent } from './pages/progressive-profile-page/progressive-profile-page.component';
import { SnackbarComponent } from './components/snackbar/snackbar.component';
import { SnackBarService } from './services/services/snackbar.service';
import { InternalUserComponent } from './components/internal-user/internal-user.component';
import { InternalUserErrorComponent } from './components/internal-user-error/internal-user-error.component';
import { DeleteConfimationDialogComponent } from './dialogs/delete-confimation-dialog/delete-confimation-dialog.component';
import { SsoComponent } from './components/sso/sso.component';
import { TosViewComponent } from './pages/tos-view/tos-view.component';
import { TopnavPlainComponent } from './components/topnavs/topnav-plain/topnav-plain.component';

const config = environment.alleAuthConfig;

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function httpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '../assets/i18n/', '.json?cacheBuster=' + environment.cacheBusterHash);
}

@NgModule({ declarations: [
        AppComponent,
        ProgressiveProfilePageComponent,
        DeleteConfimationDialogComponent,
        TopnavProgressiveProfileComponent,
        CopyrightComponent,
        CapfirstPipe,
        InputMaskDirective,
        TosCardsComponent,
        TosDialogComponent,
        FeatureFlagDirective,
        CallbackComponent,
        LoginComponent,
        OnetrustPrivacyPolicyComponent,
        OnetrustTosComponent,
        TopnavOnetrustComponent,
        SnackbarComponent,
        InternalUserComponent,
        InternalUserErrorComponent,
        SsoComponent,
        TosCardsComponent,
        TosViewComponent,
        TopnavPlainComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCardModule,
        MatCheckboxModule,
        MatDialogModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatSnackBarModule,
        MatTabsModule,
        MatToolbarModule,
        MatTooltipModule,
        AppRoutingModule,
        MatGridListModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient],
            },
        })], providers: [
        FeatureFlagService,
        OnboardAuthService,
        PersonaService,
        CountryService,
        PhoneCodeService,
        AddressService,
        ProfileApiService,
        ProfileService,
        AuthService,
        SnackBarService,
        { provide: ErrorHandler, useClass: ApplicationInsightsErrorHandler },
        Auth0Service,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptorService,
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {}
